<template>
  <b-card>
    <b-row>
      <b-col md="12">
        <h4>{{ author.name }}</h4>
        <b-badge :variant="statusVariant(author.status)" class="mr-1">{{ author.status }}</b-badge>
        <template v-if="author.status === 'pending' && $permissionAbility(OUTREACH_AUTHOR_REVIEW, permissions)">
          <b-button variant="success" size="sm" class="ml-2" @click="onStatusChange('approved')">
            Approve
          </b-button>
          <b-button variant="danger" size="sm" class="ml-1" @click="onStatusChange('rejected')">
            Reject
          </b-button>
        </template>
      </b-col>
    </b-row>

    <b-row class="mt-3">
      <b-col md="3">
        <strong>Contact:</strong>
        <p>{{ author.contact || 'N/A' }}</p>
      </b-col>
      <b-col md="3">
        <strong>Total Payment:</strong>
        <p>{{ author.total_payment }}</p>
      </b-col>
      <b-col md="3">
        <strong>Total Posts:</strong>
        <p>{{ author.total_posts_approved }}</p>
      </b-col>
      <b-col md="3">
        <strong>Avg Per Post Cost:</strong>
        <p>{{ author.per_post_cost }} <small class="text-muted">(avg)</small></p>
      </b-col>
    </b-row>

    <b-row>
      <b-col md="3">
        <strong>Posts Used:</strong>
        <p>{{ author.posts_used }}</p>
      </b-col>
      <b-col md="3">
        <strong>Posts Remaining:</strong>
        <p>{{ author.posts_remaining }}</p>
      </b-col>
      <b-col md="6">
        <strong>Notes:</strong>
        <p>{{ author.notes || 'N/A' }}</p>
      </b-col>
    </b-row>

    <hr />

    <!-- Websites -->
    <h5>Websites</h5>
    <b-table
      v-if="author.websites && author.websites.data && author.websites.data.length"
      :items="author.websites.data"
      :fields="websiteFields"
      striped
      small
    >
      <template #cell(url)="data">
        <a :href="data.item.url" target="_blank">{{ data.item.url }}</a>
      </template>
      <template #cell(traffic_value)="data">
        <span v-if="data.value != null">${{ data.value }}</span>
        <span v-else class="text-muted">N/A</span>
      </template>
      <template #cell(is_approved)="data">
        <b-badge :variant="data.value ? 'light-success' : 'light-warning'">
          {{ data.value ? 'Approved' : 'Pending' }}
        </b-badge>
      </template>
      <template #cell(action)="data">
        <b-button
          v-if="!data.item.is_approved && $permissionAbility(OUTREACH_AUTHOR_REVIEW, permissions)"
          variant="success"
          size="sm"
          @click="onApproveWebsite(data.item.id)"
        >
          Approve
        </b-button>
        <span v-else>-</span>
      </template>
    </b-table>
    <p v-else class="text-muted">No websites added.</p>

    <hr />

    <!-- Niches -->
    <h5>Approved Niches</h5>
    <div v-if="author.niches && author.niches.data && author.niches.data.length">
      <b-badge
        v-for="niche in author.niches.data"
        :key="niche.id"
        variant="light-primary"
        class="mr-1"
      >
        {{ niche.name }}
      </b-badge>
    </div>
    <p v-else class="text-muted">No niches assigned.</p>

    <hr />

    <!-- Payment History -->
    <div class="d-flex justify-content-between align-items-center pb-1">
      <h5>Payment History</h5>
      <b-button
        v-if="$permissionAbility(OUTREACH_AUTHOR_TOPUP, permissions)"
        variant="primary"
        size="sm"
        @click="showTopUpModal"
      >
        Add Top-Up
      </b-button>
    </div>

    <b-table
      v-if="historyRows.length"
      :items="historyRows"
      :fields="historyFields"
      striped
      small
      responsive
    >
      <template #cell(topup_date)="data">
        {{ formatDate(data.value) }}
      </template>
      <template #cell(status)="data">
        <b-badge :variant="historyStatusVariant(data.value)">
          {{ data.value }}
        </b-badge>
      </template>
      <template #cell(action)="data">
        <div>
          <b-button variant="info" size="sm" class="mr-1" @click="showTopUpViewModal(data.item)">
            <feather-icon icon="EyeIcon" />
          </b-button>
          <template v-if="data.item.status === 'pending' && $permissionAbility(OUTREACH_AUTHOR_REVIEW, permissions)">
            <b-button variant="success" size="sm" class="mr-1" @click="openReviewModal(data.item, 'approved')">
              <feather-icon icon="CheckIcon" />
            </b-button>
            <b-button variant="danger" size="sm" @click="openReviewModal(data.item, 'rejected')">
              <feather-icon icon="XIcon" />
            </b-button>
          </template>
          <span v-else-if="data.item.reviewer_remarks">{{ data.item.reviewer_remarks }}</span>
        </div>
      </template>
    </b-table>
    <p v-else class="text-muted">No payment history.</p>

    <div v-if="historyTotalRecords > historyPageLength" class="d-flex justify-content-end">
      <b-pagination
        v-model="historyCurrentPage"
        :total-rows="historyTotalRecords"
        :per-page="historyPageLength"
        first-number
        last-number
        align="right"
        class="mt-1 mb-0"
        @input="onHistoryPageChange"
      >
        <template #prev-text>
          <feather-icon icon="ChevronLeftIcon" size="18" />
        </template>
        <template #next-text>
          <feather-icon icon="ChevronRightIcon" size="18" />
        </template>
      </b-pagination>
    </div>

    <!-- Top-Up Modal -->
    <top-up-modal
      :author-id="authorId"
      @topup-created="onTopUpCreated"
    />

    <!-- Review Modal -->
    <b-modal
      id="modal-review"
      centered
      :title="reviewAction === 'approved' ? 'Approve Top-Up' : 'Reject Top-Up'"
      hide-footer
      no-close-on-backdrop
    >
      <p>Are you sure you want to <strong>{{ reviewAction }}</strong> this top-up?</p>
      <p v-if="reviewBatch">Amount: {{ reviewBatch.amount }} | Posts: {{ reviewBatch.posts_added }}</p>
      <b-form-group label="Reviewer Remarks">
        <b-form-textarea v-model="reviewerRemarks" placeholder="Optional remarks" rows="2" />
      </b-form-group>
      <div class="d-flex justify-content-end">
        <b-button variant="secondary" class="mr-1" @click="$bvModal.hide('modal-review')">Cancel</b-button>
        <b-button :variant="reviewAction === 'approved' ? 'success' : 'danger'" @click="submitReview">
          {{ reviewAction === 'approved' ? 'Approve' : 'Reject' }}
        </b-button>
      </div>
    </b-modal>

    <b-modal id="modal-topup-view" centered title="Top-Up Details" hide-footer>
      <div v-if="selectedTopUp" class="table-responsive">
        <table class="table table-sm">
          <tbody>
            <tr><th>Date</th><td>{{ formatDate(selectedTopUp.created_at) }}</td></tr>
            <tr><th>Top Up Date</th><td>{{ selectedTopUp.topup_date ? formatDate(selectedTopUp.topup_date) : 'N/A' }}</td></tr>
            <tr><th>Amount</th><td>{{ amountWithCurrency(selectedTopUp) }}</td></tr>
            <tr><th>Posts Added</th><td>{{ selectedTopUp.posts_added }}</td></tr>
            <tr><th>Avg Per Post Cost</th><td>{{ selectedTopUp.per_post_cost }}</td></tr>
            <tr><th>Status</th><td><b-badge :variant="historyStatusVariant(selectedTopUp.status)">{{ selectedTopUp.status }}</b-badge></td></tr>
            <tr><th>Note</th><td>{{ selectedTopUp.note || 'N/A' }}</td></tr>
            <tr v-if="selectedTopUp.reviewer_remarks"><th>Reviewer Remarks</th><td>{{ selectedTopUp.reviewer_remarks }}</td></tr>
            <tr><th>Added By</th><td>{{ selectedTopUp.created_by }}</td></tr>
            <tr v-if="selectedTopUp.reviewed_by"><th>Reviewed By</th><td>{{ selectedTopUp.reviewed_by }}</td></tr>
            <tr v-if="selectedTopUp.reviewed_at"><th>Reviewed At</th><td>{{ formatDate(selectedTopUp.reviewed_at) }}</td></tr>
            <tr>
              <th>Attachments</th>
              <td>
                <div v-if="selectedTopUp.attachments && selectedTopUp.attachments.length">
                  <div v-for="attachment in selectedTopUp.attachments" :key="attachment.id">
                    <a :href="attachment.url" target="_blank">
                      <feather-icon icon="EyeIcon" class="mr-25" />{{ attachment.file_name }}
                    </a>
                  </div>
                </div>
                <span v-else class="text-muted">No attachments.</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </b-modal>
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BBadge,
  BButton,
  BTable,
  BPagination,
  BFormSelect,
  BModal,
} from "bootstrap-vue";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { mapGetters } from "vuex";
import {
  OUTREACH_AUTHOR_SHOW,
  OUTREACH_AUTHOR_TOPUP,
  OUTREACH_AUTHOR_REVIEW,
} from "@/helpers/permissionsConstant";
import TopUpModal from "./TopUpModal.vue";

export default {
  name: "OutreachAuthorDetailView",
  components: {
    BCard,
    BRow,
    BCol,
    BBadge,
    BButton,
    BTable,
    BPagination,
    BFormSelect,
    BModal,
    TopUpModal,
  },
  data() {
    return {
      OUTREACH_AUTHOR_SHOW,
      OUTREACH_AUTHOR_TOPUP,
      OUTREACH_AUTHOR_REVIEW,
      authorId: this.$route.params.id,
      author: {},
      websiteFields: [
        "url",
        { key: "domain_authority", label: "DA" },
        "traffic",
        { key: "traffic_value", label: "Traffic Value" },
        "spam_score",
        { key: "is_approved", label: "Status" },
        { key: "action", label: "Action" },
      ],

      reviewBatch: null,
      selectedTopUp: null,
      reviewAction: "approved",
      reviewerRemarks: "",

      historyPageLength: 10,
      historyCurrentPage: 1,
      historyFields: [
        { key: "topup_date", label: "Date" },
        { key: "amount", label: "Amount" },
        { key: "posts_added", label: "Posts Added" },
        { key: "per_post_cost", label: "Avg Per Post Cost" },
        { key: "posts_used", label: "Posts Used" },
        { key: "remaining", label: "Remaining" },
        { key: "status", label: "Status" },
        { key: "created_by", label: "Added By" },
        { key: "reviewed_by", label: "Reviewed By" },
        { key: "action", label: "Action" },
      ],
      historyRows: [],
      isHistoryLoading: false,
      historyTotalRecords: 0,
      historyServerParams: {
        page: 1,
        perPage: 10,
        sort: [{ field: "created_at", type: "desc" }],
      },
    };
  },

  computed: {
    ...mapGetters({
      permissions: "userModule/getPermissions",
    }),
    statusVariant() {
      const statusColor = {
        pending: "light-primary",
        approved: "light-success",
        rejected: "light-danger",
      };
      return (status) => statusColor[status] || "light-secondary";
    },
    historyStatusVariant() {
      const statusColor = {
        pending: "light-primary",
        approved: "light-success",
        rejected: "light-danger",
      };
      return (status) => statusColor[status] || "light-secondary";
    },
  },

  async created() {
    await this.loadAuthor();
    await this.loadHistory();
  },

  methods: {
    async loadAuthor() {
      try {
        const response = await this.$api.get(`/api/outreach-authors/${this.authorId}`);
        this.author = response.data.data || {};
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            icon: "BellIcon",
            variant: "danger",
            text: error?.response?.data?.message,
          },
        });
      }
    },

    async onStatusChange(status) {
      const title = status === "approved" ? "Approve" : "Reject";
      try {
        await this.$api.put(`/api/outreach-authors/${this.authorId}/status`, {
          status,
        });
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Success",
            icon: "BellIcon",
            variant: "success",
            text: `Author ${title}d Successfully`,
          },
        });
        await this.loadAuthor();
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            icon: "BellIcon",
            variant: "danger",
            text: error?.response?.data?.message,
          },
        });
      }
    },

    async loadHistory() {
      try {
        this.isHistoryLoading = true;
        const response = await this.$api.get(
          `/api/outreach-authors/${this.authorId}/recharges`,
          {
            params: {
              show: this.historyServerParams.perPage,
              page: this.historyServerParams.page,
              sort: this.historyServerParams.sort,
            },
          }
        );

        const data = response?.data?.data;
        const meta = response?.data?.meta;

        this.historyTotalRecords = meta?.pagination?.total;
        this.historyRows = data;
        this.isHistoryLoading = false;
      } catch (error) {
        this.isHistoryLoading = false;
      }
    },

    showTopUpModal() {
      this.$bvModal.show("modal-topup");
    },

    showTopUpViewModal(item) {
      this.selectedTopUp = item;
      this.$bvModal.show("modal-topup-view");
    },

    amountWithCurrency(item) {
      const amount = parseFloat(item.amount).toFixed(2);
      const cur = item.currency;
      if (cur) {
        const label = cur.symbol || cur.currency || cur.code || "";
        return label ? `${amount} ${label}` : amount;
      }
      return amount;
    },

    async onTopUpCreated() {
      await this.loadAuthor();
      await this.loadHistory();
    },

    onHistoryPageChange(page) {
      this.historyServerParams.page = page;
      this.loadHistory();
    },

    openReviewModal(row, action) {
      this.reviewBatch = row;
      this.reviewAction = action;
      this.reviewerRemarks = "";
      this.$bvModal.show("modal-review");
    },

    async submitReview() {
      try {
        await this.$api.put(
          `/api/outreach-authors/${this.authorId}/topup/${this.reviewBatch.id}/status`,
          {
            status: this.reviewAction,
            reviewer_remarks: this.reviewerRemarks || null,
          }
        );
        this.$bvModal.hide("modal-review");
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Success",
            icon: "BellIcon",
            variant: "success",
            text: `Top-Up ${this.reviewAction === "approved" ? "Approved" : "Rejected"} Successfully`,
          },
        });
        await this.loadHistory();
        await this.loadAuthor();
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Error",
            icon: "BellIcon",
            variant: "danger",
            text: error?.response?.data?.message || "Review Failed",
          },
        });
      }
    },

    onApproveWebsite(websiteId) {
      this.$bvModal.msgBoxConfirm("Are you sure you want to approve this website?", {
        title: "Approve Website",
        size: "sm",
        okVariant: "success",
        okTitle: "Approve",
        cancelTitle: "Cancel",
        centered: true,
      }).then(async (confirmed) => {
        if (confirmed) {
          try {
            const response = await this.$api.put(`/api/outreach-authors/websites/${websiteId}/approve`);
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Success",
                icon: "BellIcon",
                variant: "success",
                text: "Website Approved Successfully",
              },
            });
            this.author = response.data.data || {};
          } catch (error) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: "Error",
                icon: "BellIcon",
                variant: "danger",
                text: error?.response?.data?.message || "Approval Failed",
              },
            });
          }
        }
      });
    },
  },
};
</script>
